import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';

import { buildUi } from '../global/uiTokens';
import {
  DetailRow,
  EmptyState,
  Loading,
  RecordCard,
  SectionCard,
  dialogPaperSx,
  fieldSx,
  outlineBtn,
  primaryBtn,
} from './userShared';

const Leave = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const { getData, addData, updateData, deleteData } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState([]);
  const [openLeaveDialog, setOpenLeaveDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelLeaveId, setCancelLeaveId] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: ''
  });
  const [loading, setLoading] = useState(true);

  const LEAVE_TYPE_LIMITS = {
    Annual:        4,
    Compassionate: 5,
    Sick:          1,
    Maternity:     40,
  };

  const NAMIBIA_HOLIDAYS_RAW = [
    // 2026
    '2026-01-01', // New Year's Day
    '2026-03-21', // Independence Day
    '2026-04-03', // Good Friday
    '2026-04-06', // Easter Monday
    '2026-05-01', // Workers' Day
    '2026-05-04', // Cassinga Day
    '2026-05-14', // Ascension Day
    '2026-05-25', // Africa Day
    '2026-08-26', // Heroes' Day
    '2026-12-10', // Human Rights Day
    '2026-12-25', // Christmas Day
    '2026-12-26', // Day of Goodwill

    // 2027
    '2027-01-01',
    '2027-03-21',
    '2027-04-03',
    '2027-04-06',
    '2027-05-01',
    '2027-05-04',
    '2027-05-14',
    '2027-05-25',
    '2027-08-26',
    '2027-12-10',
    '2027-12-25',
    '2027-12-26',
  ];

  // Shift any Sunday public holiday to the following Monday (as per the Act)
  const PUBLIC_HOLIDAYS = new Set(
    NAMIBIA_HOLIDAYS_RAW.map((dateStr) => {
      const d = new Date(dateStr);
      if (d.getDay() === 0) {
        d.setDate(d.getDate() + 1);
      }
      return d.toISOString().split('T')[0];
    })
  );


  /**
   * My leave records, and only mine.
   *
   * This is the ONLY call that returns the right rows: getData filters
   * leaves_V2 by staff_id. Every write re-runs it rather than trusting the
   * write's own response, because addData and updateData both answer with
   * "SELECT * FROM leaves_V2" - the whole table, every staff member - and
   * putting that into setLeaves is what showed everyone's requests after a
   * submit.
   */
  const loadMyLeaves = useCallback(async () => {
    const response = await getData('proccess.php', 'leaves_V2', userId);

    if (response.success) {
      setLeaves(Array.isArray(response.data) ? response.data : []);
    } else if (/not found/i.test(response.message || '')) {
      // getData answers "Record not found" instead of an empty list when a
      // staff member has no leave at all. That is an empty list, not an error -
      // and without this branch cancelling your last request would leave the
      // cancelled row on screen.
      setLeaves([]);
    }

    return response;
  }, [getData, userId]);

  useEffect(() => {
    async function fetchLeaves() {
      try {
        await loadMyLeaves();

        const [usersResponse, leaveCreditsResponse] = await Promise.all([
          getData('proccess.php', 'staffs'),
          getData('proccess.php', 'leaveCredits_V2')
        ]);

        if (usersResponse.success) {
          setStaffs(usersResponse.data);
        } else {
          toast.error(usersResponse.message || "Failed to load staff");
        }

        if (leaveCreditsResponse.success) {
          setLeaveCredits(leaveCreditsResponse.data);
        } else {
          toast.error(leaveCreditsResponse.message || "Failed to load leave credits");
        }

        
      } catch (err) {
        toast.error("Network error: Failed to fetch leaves");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchLeaves();
  }, [userId, getData, loadMyLeaves]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const countWorkingDays = (startStr, endStr) => {
    let count = 0;
    const cursor = new Date(startStr);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endStr);
    end.setHours(0, 0, 0, 0);

    while (cursor <= end) {
      const day = cursor.getDay();
      const dateStr = cursor.toISOString().split('T')[0];
      if (day !== 0 && day !== 6 && !PUBLIC_HOLIDAYS.has(dateStr)) {
        count++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();

    // Validate leave duration against type limits
        const limit = LEAVE_TYPE_LIMITS[formData.leave_type];
        if (limit !== undefined && formData.startDate && formData.endDate) {
          const days = countWorkingDays(formData.startDate, formData.endDate);
          if (days > limit) {
            toast.error(
              `${formData.leave_type} leave cannot exceed ${limit} working day${limit === 1 ? '' : 's'}. ` +
              `You selected ${days} working day${days === 1 ? '' : 's'}.`
            );
            return;
          }
        }


    try {
      const response = await addData('proccess.php', 'leaves_V2', {
        staff_id: userId,
        leave_type: formData.leave_type,
        reason: formData.reason,
        startDate: formData.startDate,
        endDate: formData.endDate,
        back_in_office: formData.back_in_office,
        department: formData.department,
        status: '0'
      });
      if (response.success) {
        toast.success("Leave request submitted successfully");
        // NOT setLeaves(response.data) - that response is the entire table.
        await loadMyLeaves();
        setOpenLeaveDialog(false);
        setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
      } else {
        toast.error(response.message || "Failed to submit leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to submit leave request");
      console.error("Leave submit error:", err);
    }
  };

  const handleEditLeave = (leave) => {
    setEditingLeave(leave);
    setFormData({
      leave_type: leave.leave_type,
      reason: leave.reason,
      startDate: leave.startDate,
      endDate: leave.endDate,
      back_in_office: leave.back_in_office,
      department: leave.department
    });
    setOpenLeaveDialog(true);
  };

  const handleUpdateLeave = async (e) => {
    e.preventDefault();
    if (!editingLeave) return;
    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        id: editingLeave.id,
        leave_type: formData.leave_type,
        reason: formData.reason,
        startDate: formData.startDate,
        endDate: formData.endDate,
        back_in_office: formData.back_in_office,
        department: formData.department,
        status: editingLeave.status
      },editingLeave.id);
      if (response.success) {
        toast.success("Leave request updated successfully");
        // Same reason as the submit above: the response is the whole table.
        await loadMyLeaves();
        setOpenLeaveDialog(false);
        setEditingLeave(null);
        setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
      } else {
        toast.error(response.message || "Failed to update leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to update leave request");
      console.error("Leave update error:", err);
    }
  };

  const handleCancelLeave = async () => {
    if (!cancelLeaveId) return;
    try {
      const response = await deleteData('proccess.php', 'leaves_V2', cancelLeaveId);
      if (response.success) {
        toast.success("Leave request canceled successfully");
        setLeaves(leaves.filter(l => l.id !== cancelLeaveId));
        setOpenCancelDialog(false);
        setCancelLeaveId(null);
      } else {
        toast.error(response.message || "Failed to cancel leave request");
      }
    } catch (err) {
      toast.error("Network error: Failed to cancel leave request");
      console.error("Leave cancel error:", err);
    }
  };

  /**
   * Filter to this user before anything else.
   *
   * The loader above already returns only their rows, so this is a second
   * guard rather than the fix. It is worth having because the whole-table write
   * response is a trap this codebase falls into repeatedly - the same bug hit
   * Profile.js - and a stray setLeaves(response.data) anywhere would otherwise
   * put every colleague's leave back on this page. String compare because MySQL
   * hands these back as strings while the prop is a number.
   */
  const myLeaves = leaves.filter(
    leave => String(leave.staff_id) === String(userId)
  );

  const recentLeaves = myLeaves.filter(leave => leave.status >= 4);
  const currentLeaves = myLeaves.filter(leave => leave.status < 4);

  return (
    <Box mt={2}>
      {loading ? (
        <Loading ui={ui} label="Loading your leave…" />
      ) : (
        <>
          <Box width="100%" display="flex" justifyContent="end">
            <Button
              startIcon={<EventIcon />}
              onClick={() => {
                setEditingLeave(null);
                setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
                setOpenLeaveDialog(true);
              }}
              sx={{ ...primaryBtn(ui), mb: 2 }}
            >
              Apply for Leave
            </Button>
          </Box>

          <Box display="flex" justifyContent="space-between" flexWrap="wrap-reverse">
            <Box flex="1" maxWidth="1000px" minWidth="350px" m="0.3rem">
              <SectionCard
                ui={ui}
                title="Recent leaves"
                hint="Requests that have been settled."
              >
                {recentLeaves.length > 0 ? (
                  recentLeaves.map((leave) => (
                    <RecordCard
                      ui={ui}
                      colors={colors}
                      key={leave.id}
                      status={leave.status}
                      title={`${leave.leave_type} leave`}
                    >
                      <DetailRow
                        ui={ui}
                        label="Dates"
                        value={`${leave.startDate} → ${leave.endDate}`}
                      />
                      <DetailRow
                        ui={ui}
                        label="Back in office"
                        value={leave.back_in_office}
                        color={ui.accent}
                      />
                      <DetailRow
                        ui={ui}
                        label="Decided on"
                        value={leave.supervisor_appr_date || leave.rejected_date}
                      />
                      <DetailRow ui={ui} label="Comments" value={leave.comments} />
                    </RecordCard>
                  ))
                ) : (
                  <EmptyState
                    ui={ui}
                    icon={<EventBusyOutlinedIcon />}
                    title="Nothing settled yet"
                    hint="Approved and rejected requests appear here."
                  />
                )}
              </SectionCard>
            </Box>

            <Box flex="1" maxWidth="700px" minWidth="350px" m="0.3rem">
              <SectionCard
                ui={ui}
                title="Current requests"
                hint="Still with your supervisor. Pending ones can be changed."
              >
                {currentLeaves.length > 0 ? (
                  currentLeaves.map((leave) => (
                    <RecordCard
                      ui={ui}
                      colors={colors}
                      key={leave.id}
                      status={leave.status}
                      title={`${leave.leave_type} leave`}
                      action={
                        // Only before a supervisor has acted on it.
                        leave.status < 2 ? (
                          <>
                            <Tooltip title="Edit this request">
                              <IconButton
                                size="small"
                                onClick={() => handleEditLeave(leave)}
                                sx={{ color: ui.accent }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel this request">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCancelLeaveId(leave.id);
                                  setOpenCancelDialog(true);
                                }}
                                sx={{ color: colors.red[500] }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null
                      }
                    >
                      <DetailRow
                        ui={ui}
                        label="Dates"
                        value={`${leave.startDate} → ${leave.endDate}`}
                      />
                      <DetailRow
                        ui={ui}
                        label="Back in office"
                        value={leave.back_in_office}
                        color={ui.accent}
                      />
                      <DetailRow
                        ui={ui}
                        label="Decided on"
                        value={leave.supervisor_appr_date || leave.rejected_date}
                      />
                      <DetailRow ui={ui} label="Comments" value={leave.comments} />
                    </RecordCard>
                  ))
                ) : (
                  <EmptyState
                    ui={ui}
                    icon={<EventIcon />}
                    title="No open requests"
                    hint="Use Apply for Leave to raise one."
                  />
                )}
              </SectionCard>
            </Box>
          </Box>

          <Dialog
            open={openLeaveDialog}
            onClose={() => {
              setOpenLeaveDialog(false);
              setEditingLeave(null);
              setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: dialogPaperSx(ui) }}
          >
            <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
              {editingLeave ? 'Edit leave request' : 'Apply for leave'}
            </DialogTitle>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogContent sx={{ pt: 3 }}>
              <Box component="form" onSubmit={editingLeave ? handleUpdateLeave : handleLeaveSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                                select
                                style={{ width: "100%" }}
                                id="leave_type"
                                name="leave_type"
                                label="Leave Type"
                                value={formData.leave_type}
                                onChange={handleFormChange}
                                sx={fieldSx(ui)}
                                required
                                SelectProps={{ native: true }}
                                helperText={
                                  formData.leave_type && LEAVE_TYPE_LIMITS[formData.leave_type] !== undefined
                                    ? `Maximum: ${LEAVE_TYPE_LIMITS[formData.leave_type]} working day${LEAVE_TYPE_LIMITS[formData.leave_type] === 1 ? '' : 's'}`
                                    : ''
                                }
                              >
                                <option value=""></option>
                                <option value="Annual">Annual</option>
                                <option value="Sick">Sick</option>
                                <option value="Compassionate">Compassionate</option>
                                <option value="Study">Study</option>
                                <option value="Maternity">Maternity</option>
                              </TextField>

                <TextField
                  label="Reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  multiline
                  rows={2}
                  required
                />
                <TextField
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                                style={{ width: "100%" }}
                                id="endDate"
                                name="endDate"
                                label="End Date"
                                type="date"
                                value={formData.endDate}
                                onChange={handleFormChange}
                                sx={fieldSx(ui)}
                                required
                                InputLabelProps={{ shrink: true }}
                                helperText={
                                  formData.startDate && formData.endDate
                                    ? (() => {
                                        const days = countWorkingDays(formData.startDate, formData.endDate);
                                        const limit = LEAVE_TYPE_LIMITS[formData.leave_type];
                                        const over  = limit !== undefined && days > limit;
                                        return (
                                          <span style={{ color: over ? 'red' : 'inherit' }}>
                                            {days} working day{days === 1 ? '' : 's'} selected
                                            {over ? ` — exceeds ${limit}-day limit` : ''}
                                          </span>
                                        );
                                      })()
                                    : ''
                                }
                              />
                <TextField
                  label="Back in Office"
                  name="back_in_office"
                  type="date"
                  value={formData.back_in_office}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  select
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="ICT">ICT</option>
                  <option value="SCIENCE">SCIENCE</option>
                  <option value="ART">ART</option>
                  <option value="HR">HR</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="STEAM">STEAM</option>
                  <option value="ENGINEERING">ENGINEERING</option>
                  <option value="HYGIENE">HYGIENE</option>
                </TextField>
              </Box>
            </DialogContent>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogActions sx={{ px: 3, py: 2.5 }}>
              <Button
                onClick={() => {
                  setOpenLeaveDialog(false);
                  setEditingLeave(null);
                  setFormData({ leave_type: '', reason: '', startDate: '', endDate: '', back_in_office: '', department: '' });
                }}
                sx={{ textTransform: 'none', color: ui.textSecondary }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={editingLeave ? handleUpdateLeave : handleLeaveSubmit}
                endIcon={<SendIcon />}
                sx={primaryBtn(ui)}
              >
                {editingLeave ? 'Update' : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openCancelDialog}
            onClose={() => {
              setOpenCancelDialog(false);
              setCancelLeaveId(null);
            }}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: dialogPaperSx(ui) }}
          >
            <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
              Cancel this request?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color={ui.textSecondary}>
                The request will be withdrawn. You can apply again afterwards.
              </Typography>
            </DialogContent>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogActions sx={{ px: 3, py: 2.5 }}>
              <Button
                onClick={() => {
                  setOpenCancelDialog(false);
                  setCancelLeaveId(null);
                }}
                sx={{ textTransform: 'none', color: ui.textSecondary }}
              >
                Keep it
              </Button>
              <Button onClick={handleCancelLeave} sx={outlineBtn(ui, colors.red[500])}>
                Yes, cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Leave;